import type { ProductDto } from '../api/productApi';

export interface ProductVariant {
  product: ProductDto;
  /** Customer-facing choice, e.g. "3 months" or "1 year". */
  label: string;
  /** Common unit used only to put duration choices in a natural order. */
  durationMonths: number;
}

export interface ProductListing {
  /** Stable React identity. Group keys are derived from category and base name. */
  key: string;
  /** Name shown once on the storefront card. */
  name: string;
  /** The initially selected product when the sheet opens. */
  primary: ProductDto;
  /** One entry for normal products; two or more for a grouped product. */
  variants: ProductVariant[];
}

interface ParsedDuration {
  baseName: string;
  label: string;
  durationMonths: number;
}

const UNIT_MONTHS: Record<string, number> = {
  m: 1,
  mo: 1,
  mos: 1,
  month: 1,
  months: 1,
  y: 12,
  yr: 12,
  yrs: 12,
  year: 12,
  years: 12,
};

/**
 * Pulls a duration from either end of a product name.
 *
 * Both forms exist in supplier catalogues ("Telegram Premium 3 Months" and
 * "3 Month Spotify Premium Subscription"), so grouping only one would make
 * the feature depend on how the upstream happened to word a title.
 */
function parseDuration(name: string): ParsedDuration | null {
  const suffix = name.match(
    /^(.*?)(?:\s*[-–—|:/]\s*|\s+)(\d+)\s*(months?|mos?|m|years?|yrs?|y)\s*$/i,
  );
  const prefix = name.match(
    /^(\d+)\s*(months?|mos?|m|years?|yrs?|y)(?:\s*[-–—|:/]\s*|\s+)(.+)$/i,
  );

  const amount = Number(suffix?.[2] ?? prefix?.[1]);
  const rawUnit = (suffix?.[3] ?? prefix?.[2])?.toLowerCase();
  const baseName = (suffix?.[1] ?? prefix?.[3])?.trim();
  const multiplier = rawUnit ? UNIT_MONTHS[rawUnit] : undefined;

  if (!baseName || !Number.isInteger(amount) || amount <= 0 || !multiplier) return null;

  const yearly = multiplier === 12;
  return {
    baseName,
    label: `${amount} ${yearly ? (amount === 1 ? 'year' : 'years') : amount === 1 ? 'month' : 'months'}`,
    durationMonths: amount * multiplier,
  };
}

function normalizedGroupKey(product: ProductDto, baseName: string): string {
  const normalized = baseName
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  // Products in different storefront sections should never be collapsed
  // together even when an upstream supplier gives them the same name.
  return `${product.category?.id ?? 'uncategorised'}:${normalized}`;
}

/**
 * Collapses duration variants into one storefront listing.
 *
 * A duration-looking name is not enough by itself: grouping starts only when
 * at least two products have the same base name in the same category. This
 * leaves standalone titles such as "Google AI Pro 12M" exactly as they are.
 */
export function groupProductVariants(products: ProductDto[]): ProductListing[] {
  const parsed = new Map<string, ParsedDuration>();
  const groupMembers = new Map<string, ProductDto[]>();

  for (const product of products) {
    const duration = parseDuration(product.name);
    if (!duration) continue;

    parsed.set(product.id, duration);
    const key = normalizedGroupKey(product, duration.baseName);
    groupMembers.set(key, [...(groupMembers.get(key) ?? []), product]);
  }

  const emitted = new Set<string>();
  const listings: ProductListing[] = [];

  for (const product of products) {
    const duration = parsed.get(product.id);
    const groupKey = duration ? normalizedGroupKey(product, duration.baseName) : null;
    const members = groupKey ? groupMembers.get(groupKey) ?? [] : [];

    if (!duration || !groupKey || members.length < 2) {
      listings.push({
        key: product.id,
        name: product.name,
        primary: product,
        variants: [{ product, label: product.name, durationMonths: 0 }],
      });
      continue;
    }

    if (emitted.has(groupKey)) continue;
    emitted.add(groupKey);

    const variants = members
      .map((member) => {
        const memberDuration = parsed.get(member.id)!;
        return {
          product: member,
          label: memberDuration.label,
          durationMonths: memberDuration.durationMonths,
        };
      })
      .sort(
        (a, b) =>
          a.durationMonths - b.durationMonths || a.product.name.localeCompare(b.product.name),
      );
    const primary =
      variants.find((variant) => variant.product.inStock)?.product ?? variants[0]!.product;

    listings.push({
      key: `variants:${groupKey}`,
      name: duration.baseName,
      primary,
      variants,
    });
  }

  return listings;
}
